import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getService, createService, updateService } from './adminApi';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const FIELD_TYPES = [
    { v: 'text', label: 'Строка' },
    { v: 'textarea', label: 'Текст (многострочный)' },
    { v: 'number', label: 'Число' },
    { v: 'email', label: 'Email' },
    { v: 'phone', label: 'Телефон' },
    { v: 'select', label: 'Список (выбор)' },
];

const EMPTY = {
    name: '',
    description: '',
    service_type: 'N_DAYS_FORWARD',
    is_active: true,
    club_selection_enabled: true,
    block_truenas_clubs: false,
    days_forward: 30,
    days_before_start: 0,
    days_before_end: null,
    registration_opens_at: '',
    registration_closes_at: '',
    default_max_bookings: 1,
    max_bookings_per_club: null,
    one_booking_per_club: false,
    max_per_day: null,
    slots: [],
    date_ranges: [],
    excluded_weekdays: [],
    excluded_dates: [],
    custom_fields: [],
};

const isoToLocal = (iso) => (iso ? String(iso).slice(0, 16) : '');
const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function genTimes(from, to, stepMin) {
    const [fh, fm] = from.split(':').map(Number);
    const [th, tm] = to.split(':').map(Number);
    const start = fh * 60 + fm;
    const end = th * 60 + tm;
    const step = Math.max(1, Number(stepMin) || 60);
    const out = [];
    for (let t = start; t <= end; t += step) {
        const h = String(Math.floor(t / 60)).padStart(2, '0');
        const m = String(t % 60).padStart(2, '0');
        out.push(`${h}:${m}`);
    }
    return out;
}

export default function ServiceEditor() {
    const { id } = useParams();
    const editing = !!id;
    const navigate = useNavigate();

    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // slot generator inputs
    const [gen, setGen] = useState({ weekdays: [1, 2, 3, 4, 5], from: '10:00', to: '20:00', step: 60, cap: 1 });

    useEffect(() => {
        if (!editing) return;
        getService(id)
            .then((s) => {
                setForm({
                    ...EMPTY,
                    ...s,
                    registration_opens_at: isoToLocal(s.registration_opens_at),
                    registration_closes_at: isoToLocal(s.registration_closes_at),
                    date_ranges: (s.date_ranges || []).map((r) => ({ start_date: r.start_date, end_date: r.end_date, slots: r.slots || [] })),
                    excluded_dates: s.excluded_dates || [],
                    custom_fields: (s.custom_fields || []).map((c) => ({ ...c, options: c.options || [] })),
                });
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [id, editing]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const toggleGenWeekday = (i) => setGen((g) => ({
        ...g,
        weekdays: g.weekdays.includes(i) ? g.weekdays.filter((x) => x !== i) : [...g.weekdays, i],
    }));

    const addGeneratedSlots = () => {
        const times = genTimes(gen.from, gen.to, gen.step);
        const cap = Math.max(1, Number(gen.cap) || 1);
        setForm((f) => {
            const key = (s) => `${s.weekday}-${s.time}`;
            const map = new Map(f.slots.map((s) => [key(s), s]));
            gen.weekdays.forEach((wd) => times.forEach((t) => {
                map.set(`${wd}-${t}`, { weekday: wd, time: t, max_bookings: cap });
            }));
            return { ...f, slots: Array.from(map.values()).sort((a, b) => a.weekday - b.weekday || a.time.localeCompare(b.time)) };
        });
    };

    const removeSlot = (s) => setForm((f) => ({ ...f, slots: f.slots.filter((x) => !(x.weekday === s.weekday && x.time === s.time)) }));
    const clearSlots = () => set({ slots: [] });

    const toggleExcludedWeekday = (i) => setForm((f) => ({
        ...f,
        excluded_weekdays: f.excluded_weekdays.includes(i) ? f.excluded_weekdays.filter((x) => x !== i) : [...f.excluded_weekdays, i],
    }));

    // custom fields
    const addField = () => setForm((f) => ({ ...f, custom_fields: [...f.custom_fields, { label: '', field_type: 'text', required: false, options: [], order: f.custom_fields.length }] }));
    const updField = (i, patch) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
    const rmField = (i) => setForm((f) => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) }));

    // date ranges
    const addRange = () => setForm((f) => ({ ...f, date_ranges: [...f.date_ranges, { start_date: '', end_date: '', slots: [] }] }));
    const updRange = (i, patch) => setForm((f) => ({ ...f, date_ranges: f.date_ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
    const rmRange = (i) => setForm((f) => ({ ...f, date_ranges: f.date_ranges.filter((_, j) => j !== i) }));

    // excluded dates
    const addExDate = () => setForm((f) => ({ ...f, excluded_dates: [...f.excluded_dates, { date: '', reason: '' }] }));
    const updExDate = (i, patch) => setForm((f) => ({ ...f, excluded_dates: f.excluded_dates.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
    const rmExDate = (i) => setForm((f) => ({ ...f, excluded_dates: f.excluded_dates.filter((_, j) => j !== i) }));

    const slotsByWeekday = useMemo(() => {
        const g = {};
        form.slots.forEach((s) => { (g[s.weekday] = g[s.weekday] || []).push(s); });
        return g;
    }, [form.slots]);

    const submit = async () => {
        setError('');
        if (!form.name.trim()) { setError('Укажите название сервиса'); return; }
        const payload = {
            name: form.name.trim(),
            description: form.description || null,
            service_type: form.service_type,
            is_active: form.is_active,
            club_selection_enabled: form.club_selection_enabled,
            block_truenas_clubs: form.block_truenas_clubs,
            days_forward: numOrNull(form.days_forward),
            days_before_start: numOrNull(form.days_before_start),
            days_before_end: numOrNull(form.days_before_end),
            registration_opens_at: form.registration_opens_at || null,
            registration_closes_at: form.registration_closes_at || null,
            default_max_bookings: Number(form.default_max_bookings) || 1,
            max_bookings_per_club: numOrNull(form.max_bookings_per_club),
            one_booking_per_club: !!form.one_booking_per_club,
            max_per_day: numOrNull(form.max_per_day),
            slots: form.slots.map((s) => ({ weekday: s.weekday, time: s.time, max_bookings: Number(s.max_bookings) || 1 })),
            date_ranges: form.service_type === 'DATE_RANGES'
                ? form.date_ranges.filter((r) => r.start_date && r.end_date).map((r) => ({ start_date: r.start_date, end_date: r.end_date, slots: [] }))
                : [],
            excluded_weekdays: form.excluded_weekdays,
            excluded_dates: form.excluded_dates.filter((r) => r.date).map((r) => ({ date: r.date, reason: r.reason || null })),
            custom_fields: form.custom_fields
                .filter((c) => c.label.trim())
                .map((c, i) => ({
                    label: c.label.trim(),
                    field_type: c.field_type,
                    required: !!c.required,
                    order: i,
                    options: c.field_type === 'select' ? (c.options || []).filter(Boolean) : null,
                })),
        };
        setSaving(true);
        try {
            if (editing) await updateService(id, payload);
            else await createService(payload);
            navigate('/admin/services');
        } catch (e) {
            setError(e.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="a-muted">Загрузка…</div>;

    return (
        <div className="a-editor">
            <div className="a-page-head">
                <h1>{editing ? 'Редактирование сервиса' : 'Новый сервис'}</h1>
                <div>
                    <button className="a-btn" onClick={() => navigate('/admin/services')}>Отмена</button>
                    <button className="a-btn a-btn-primary" onClick={submit} disabled={saving}>
                        {saving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                </div>
            </div>

            {error && <div className="a-error">{error}</div>}

            {/* ── Основное ── */}
            <section className="a-section">
                <h2>Основное</h2>
                <div className="a-field">
                    <label>Название *</label>
                    <input className="a-input" value={form.name} onChange={(e) => set({ name: e.target.value })} />
                </div>
                <div className="a-field">
                    <label>Описание</label>
                    <textarea className="a-input" rows={3} value={form.description || ''} onChange={(e) => set({ description: e.target.value })} />
                </div>
                <div className="a-row">
                    <label className="a-check">
                        <input type="checkbox" checked={form.is_active} onChange={(e) => set({ is_active: e.target.checked })} />
                        Сервис активен (доступен для записи)
                    </label>
                </div>
            </section>

            {/* ── Клуб ── */}
            <section className="a-section">
                <h2>Выбор клуба</h2>
                <label className="a-check">
                    <input type="checkbox" checked={form.club_selection_enabled} onChange={(e) => set({ club_selection_enabled: e.target.checked })} />
                    Требовать выбор клуба при записи
                </label>
                <label className="a-check">
                    <input type="checkbox" checked={form.block_truenas_clubs} onChange={(e) => set({ block_truenas_clubs: e.target.checked })} />
                    Запретить клубы с пометкой TrueNas
                </label>
            </section>

            {/* ── Доступность ── */}
            <section className="a-section">
                <h2>Доступность записи</h2>
                <div className="a-field">
                    <label>Тип расписания</label>
                    <select className="a-input" value={form.service_type} onChange={(e) => {
                        const t = e.target.value;
                        // WEEKLY ignores the date-window / registration-window fields;
                        // clear them so stale values don't gate an abstract weekly schedule.
                        set(t === 'WEEKLY'
                            ? { service_type: t, registration_opens_at: '', registration_closes_at: '', days_before_start: 0 }
                            : { service_type: t });
                    }}>
                        <option value="N_DAYS_FORWARD">N дней вперёд (от сегодня)</option>
                        <option value="WEEKLY">Еженедельно (по дням недели)</option>
                        <option value="DATE_RANGES">Конкретные диапазоны дат</option>
                    </select>
                </div>

                {form.service_type !== 'WEEKLY' && (
                    <div className="a-grid3">
                        <div className="a-field">
                            <label>Доступно начиная с (дней от сегодня)</label>
                            <input className="a-input" type="number" min="0" value={form.days_before_start ?? ''} onChange={(e) => set({ days_before_start: e.target.value })} />
                        </div>
                        {form.service_type === 'N_DAYS_FORWARD' && (
                            <div className="a-field">
                                <label>Доступно до (дней вперёд)</label>
                                <input className="a-input" type="number" min="0" value={form.days_forward ?? ''} onChange={(e) => set({ days_forward: e.target.value })} />
                            </div>
                        )}
                        {form.service_type === 'DATE_RANGES' && (
                            <div className="a-field">
                                <label>Не позднее (дней вперёд, опц.)</label>
                                <input className="a-input" type="number" min="0" value={form.days_before_end ?? ''} onChange={(e) => set({ days_before_end: e.target.value })} />
                            </div>
                        )}
                    </div>
                )}

                {form.service_type === 'DATE_RANGES' && (
                    <div className="a-sub">
                        <div className="a-sub-head">
                            <span>Диапазоны дат</span>
                            <button className="a-btn a-btn-sm" onClick={addRange}>+ Диапазон</button>
                        </div>
                        {form.date_ranges.map((r, i) => (
                            <div className="a-row" key={i}>
                                <input className="a-input" type="date" value={r.start_date} onChange={(e) => updRange(i, { start_date: e.target.value })} />
                                <span className="a-muted">—</span>
                                <input className="a-input" type="date" value={r.end_date} onChange={(e) => updRange(i, { end_date: e.target.value })} />
                                <button className="a-btn a-btn-danger a-btn-sm" onClick={() => rmRange(i)}>×</button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Excluded weekdays */}
                <div className="a-field">
                    <label>Исключить дни недели</label>
                    <div className="a-weekdays">
                        {WEEKDAYS.map((w, i) => (
                            <button
                                type="button"
                                key={i}
                                className={`a-chip ${form.excluded_weekdays.includes(i) ? 'a-chip-on' : ''}`}
                                onClick={() => toggleExcludedWeekday(i)}
                            >{w}</button>
                        ))}
                    </div>
                </div>

                {/* Excluded dates */}
                <div className="a-sub">
                    <div className="a-sub-head">
                        <span>Исключить конкретные даты</span>
                        <button className="a-btn a-btn-sm" onClick={addExDate}>+ Дата</button>
                    </div>
                    {form.excluded_dates.map((r, i) => (
                        <div className="a-row" key={i}>
                            <input className="a-input" type="date" value={r.date} onChange={(e) => updExDate(i, { date: e.target.value })} />
                            <input className="a-input" placeholder="Причина (опц.)" value={r.reason || ''} onChange={(e) => updExDate(i, { reason: e.target.value })} />
                            <button className="a-btn a-btn-danger a-btn-sm" onClick={() => rmExDate(i)}>×</button>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Окно регистрации ── */}
            {form.service_type !== 'WEEKLY' && (
                <section className="a-section">
                    <h2>Окно регистрации</h2>
                    <p className="a-muted">Когда форма записи открыта для гостей. Пусто — без ограничения.</p>
                    <div className="a-grid2">
                        <div className="a-field">
                            <label>Открытие</label>
                            <input className="a-input" type="datetime-local" value={form.registration_opens_at} onChange={(e) => set({ registration_opens_at: e.target.value })} />
                        </div>
                        <div className="a-field">
                            <label>Закрытие</label>
                            <input className="a-input" type="datetime-local" value={form.registration_closes_at} onChange={(e) => set({ registration_closes_at: e.target.value })} />
                        </div>
                    </div>
                </section>
            )}

            {/* ── Слоты / расписание ── */}
            <section className="a-section">
                <h2>Расписание (слоты)</h2>
                <p className="a-muted">Разбивка по дням недели и часам. Сгенерируйте слоты по диапазону часов и интервалу.</p>
                <div className="a-generator">
                    <div className="a-weekdays">
                        {WEEKDAYS.map((w, i) => (
                            <button type="button" key={i} className={`a-chip ${gen.weekdays.includes(i) ? 'a-chip-on' : ''}`} onClick={() => toggleGenWeekday(i)}>{w}</button>
                        ))}
                    </div>
                    <div className="a-grid4">
                        <div className="a-field"><label>С</label><input className="a-input" type="time" value={gen.from} onChange={(e) => setGen({ ...gen, from: e.target.value })} /></div>
                        <div className="a-field"><label>По</label><input className="a-input" type="time" value={gen.to} onChange={(e) => setGen({ ...gen, to: e.target.value })} /></div>
                        <div className="a-field"><label>Интервал, мин</label><input className="a-input" type="number" min="5" step="5" value={gen.step} onChange={(e) => setGen({ ...gen, step: e.target.value })} /></div>
                        <div className="a-field"><label>Мест на слот (вместимость)</label><input className="a-input" type="number" min="1" value={gen.cap} onChange={(e) => setGen({ ...gen, cap: e.target.value })} /></div>
                    </div>
                    <div className="a-row">
                        <button className="a-btn a-btn-primary a-btn-sm" onClick={addGeneratedSlots}>Сгенерировать и добавить</button>
                        {form.slots.length > 0 && <button className="a-btn a-btn-danger a-btn-sm" onClick={clearSlots}>Очистить все</button>}
                    </div>
                </div>

                {form.slots.length === 0
                    ? <div className="a-empty">Слоты не заданы.</div>
                    : (
                        <div className="a-slots">
                            {WEEKDAYS.map((w, wd) => (slotsByWeekday[wd] ? (
                                <div className="a-slot-day" key={wd}>
                                    <div className="a-slot-day-name">{w}</div>
                                    <div className="a-slot-chips">
                                        {slotsByWeekday[wd].sort((a, b) => a.time.localeCompare(b.time)).map((s) => (
                                            <span className="a-slot-chip" key={s.time}>
                                                {s.time} · {s.max_bookings}
                                                <button onClick={() => removeSlot(s)}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ) : null))}
                        </div>
                    )}
            </section>

            {/* ── Лимиты ── */}
            <section className="a-section">
                <h2>Лимиты</h2>
                <p className="a-muted">Вместимость каждого слота (сколько записей влезает на одно время) задаётся в блоке «Расписание». Здесь — ограничения поверх этого.</p>
                <label className="a-check">
                    <input type="checkbox" checked={form.one_booking_per_club} onChange={(e) => set({ one_booking_per_club: e.target.checked })} />
                    1 клуб = 1 запись (от одного клуба разрешена только одна запись)
                </label>
                <div className="a-grid2">
                    <div className="a-field">
                        <label>Лимит записей на клуб (опц.)</label>
                        <input className="a-input" type="number" min="1" disabled={form.one_booking_per_club}
                            value={form.one_booking_per_club ? '' : (form.max_bookings_per_club ?? '')}
                            onChange={(e) => set({ max_bookings_per_club: e.target.value })} />
                    </div>
                    <div className="a-field">
                        <label>Лимит записей в день (опц.)</label>
                        <input className="a-input" type="number" min="1" value={form.max_per_day ?? ''} onChange={(e) => set({ max_per_day: e.target.value })} />
                    </div>
                </div>
            </section>

            {/* ── Доп. поля ── */}
            <section className="a-section">
                <div className="a-sub-head">
                    <h2>Дополнительные поля формы</h2>
                    <button className="a-btn a-btn-sm" onClick={addField}>+ Поле</button>
                </div>
                {form.custom_fields.length === 0 && <div className="a-empty">Дополнительных полей нет.</div>}
                {form.custom_fields.map((c, i) => (
                    <div className="a-cfield" key={i}>
                        <div className="a-grid3">
                            <div className="a-field">
                                <label>Название поля</label>
                                <input className="a-input" value={c.label} onChange={(e) => updField(i, { label: e.target.value })} />
                            </div>
                            <div className="a-field">
                                <label>Тип</label>
                                <select className="a-input" value={c.field_type} onChange={(e) => updField(i, { field_type: e.target.value })}>
                                    {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                                </select>
                            </div>
                            <div className="a-field a-field-check">
                                <label className="a-check">
                                    <input type="checkbox" checked={c.required} onChange={(e) => updField(i, { required: e.target.checked })} />
                                    Обязательное
                                </label>
                                <button className="a-btn a-btn-danger a-btn-sm" onClick={() => rmField(i)}>Удалить поле</button>
                            </div>
                        </div>
                        {c.field_type === 'select' && (
                            <div className="a-field">
                                <label>Варианты (через запятую)</label>
                                <input
                                    className="a-input"
                                    value={(c.options || []).join(', ')}
                                    onChange={(e) => updField(i, { options: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                                    placeholder="Вариант 1, Вариант 2, Вариант 3"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </section>

            <div className="a-editor-foot">
                <button className="a-btn" onClick={() => navigate('/admin/services')}>Отмена</button>
                <button className="a-btn a-btn-primary" onClick={submit} disabled={saving}>
                    {saving ? 'Сохранение…' : 'Сохранить сервис'}
                </button>
            </div>
        </div>
    );
}
